/**
 * Single source of truth for tournament format metadata + dispatch keys.
 *
 * This is the fresh registry for the src/tournament_formats rebuild — it does not
 * import from or extend the legacy scattered definitions in
 * tournamentScheduleGenerator.js / compassDrawScheduler.js / customFormatScheduleGenerator.js /
 * mlpScheduler.js (those are frozen, see KYN-475). New formats get added here, and only here.
 *
 * First pass covers 6 formats chosen to exercise every result-view pattern:
 *  - single_elimination -> pure graphical (bracket only)
 *  - round_robin        -> pure list (no bracket shape exists)
 *  - group_knockout     -> hybrid (groups list -> knockout bracket), needs grouping input
 *  - compass_draw       -> pure graphical, multi-bracket, losers DIVERGE into feeder brackets
 *  - regional_bracket   -> pure graphical, multi-bracket, region WINNERS CONVERGE into a final bracket
 *  - page_playoff       -> hybrid (league list -> fixed top-4 "double chance" ladder), no grouping
 *  - ladder             -> its own view (ranked standings + one opening round) — see ladderGenerator.js;
 *                          the one format where most of the schedule is genuinely result-dependent,
 *                          not something a generator can produce up front
 */

export const RESULT_VIEW = {
  GRAPHICAL: 'graphical',
  LIST: 'list',
  HYBRID: 'hybrid',
};

export const TOURNAMENT_FORMATS = [
  {
    id: 'single_elimination',
    label: 'Single Elimination',
    shortLabel: 'Knockout',
    tagline: 'One loss and you\'re out',
    description: 'The classic bracket. Win and advance, lose and you\'re done — fastest path to a champion.',
    icon: 'GitBranch',
    minParticipants: 2,
    maxParticipants: 128,
    needsGrouping: false,
    resultView: RESULT_VIEW.GRAPHICAL,
    participantLabel: 'participants',
  },
  {
    id: 'round_robin',
    label: 'Round Robin',
    shortLabel: 'Round Robin',
    tagline: 'Everyone plays everyone',
    description: 'No brackets, no eliminations — every participant faces every other participant. Ranked by record.',
    icon: 'Repeat',
    minParticipants: 3,
    maxParticipants: 32,
    needsGrouping: false,
    supportsLegs: true, // e.g. a "double round robin" (2 legs, home & away) — plumbed through generateRoundRobin's existing legs option
    resultView: RESULT_VIEW.LIST,
    participantLabel: 'participants',
  },
  {
    id: 'group_knockout',
    label: 'Group Stage + Knockout',
    shortLabel: 'Groups + Knockout',
    tagline: 'Pool play, then single elimination',
    description: 'Participants are split into groups for round-robin pool play, then top finishers advance into a knockout bracket.',
    icon: 'Layers',
    minParticipants: 8,
    maxParticipants: 128,
    needsGrouping: true,
    groupUnitLabel: 'Group',
    fixedAdvancePerGroup: null, // configurable, defaults to 2
    resultView: RESULT_VIEW.HYBRID,
    participantLabel: 'participants',
  },
  {
    id: 'regional_bracket',
    label: 'Regional Bracket',
    shortLabel: 'Regional Bracket',
    tagline: 'Split into regions, winners meet at the top',
    description: 'Participants are split into named regions, each playing down to a regional champion — then the champions meet in a Final Four-style bracket to crown one overall winner. The March Madness format.',
    icon: 'Trophy',
    // Matches regionalBracketGenerator.js's actual floor (2 regions x 2) —
    // not a stricter UI-only suggestion. 8 felt like a reasonable default
    // back when this only ran on individual singles/doubles participants,
    // but it wrongly blocked a perfectly valid smaller bracket once
    // participants can be teams (e.g. 4 teams, 2 per region).
    minParticipants: 4,
    maxParticipants: 128,
    needsGrouping: true,
    groupUnitLabel: 'Region',
    fixedAdvancePerGroup: 1, // always exactly the region's winner — not a configurable rank
    resultView: RESULT_VIEW.GRAPHICAL,
    participantLabel: 'participants',
  },
  {
    id: 'page_playoff',
    label: 'Page Playoff',
    shortLabel: 'Page Playoff',
    tagline: 'Top seeds get a second chance',
    description: 'Everyone plays a round-robin league (single round-robin by default, adjustable to double round-robin/home & away after generating), then the top 4 head into a Qualifier/Eliminator ladder — seeds 1-2 get a "double chance" (lose once, still get another shot at the Final) while seeds 3-4 face a straight knockout. Also known as the IPL Playoff or Double Chance Playoff.',
    icon: 'Award',
    minParticipants: 4,
    maxParticipants: 32,
    needsGrouping: false,
    supportsLegs: true, // e.g. a "double round robin" (2 legs, home & away) — plumbed through generatePagePlayoff's existing legs option
    resultView: RESULT_VIEW.HYBRID,
    participantLabel: 'participants',
  },
  {
    id: 'cross_group',
    label: 'Dual Meet / Series',
    shortLabel: 'Dual Meet',
    tagline: 'One side faces the other — Ryder Cup or Davis Cup style',
    description: 'Sides are split into groups (e.g. Region A vs Region B, or just two nations) — every team plays every team in the OTHER group(s), never its own. Set "Matches per pairing" above 1 on the generated schedule screen for a multi-match series between the same two sides (e.g. a 3-Test series). Each individual match can optionally expand into the MLP 5-game slate via the Tie Format toggle once generated.',
    icon: 'Swords',
    // Matches the generator's real floor — 2 groups of 1 each (a pure
    // bilateral series, e.g. India vs Australia) is the smallest valid case.
    minParticipants: 2,
    maxParticipants: 64,
    needsGrouping: true,
    groupUnitLabel: 'Group',
    fixedGroupCount: 2, // always exactly 2 sides — a "dual" meet, not a scaling number of pools
    hideAdvanceStep: true, // no "advance" concept — groups don't play internally, only across
    requiresTeamRoster: true, // participants are 4-player team rosters, not singles/doubles slots
    supportsLegs: true, // repeat count per pairing — the India-vs-Australia "3-match series" case
    resultView: RESULT_VIEW.LIST,
    participantLabel: 'teams',
  },
  {
    id: 'compass_draw',
    label: 'Compass Draw',
    shortLabel: 'Compass Draw',
    tagline: 'Nobody goes home after one loss',
    description: 'Losers drop into direction-named consolation brackets (W, N, S, E, NW...) instead of being eliminated outright. Bracket shape is fully derived from participant count.',
    icon: 'Compass',
    minParticipants: 4,
    maxParticipants: 128,
    needsGrouping: false,
    resultView: RESULT_VIEW.GRAPHICAL,
    participantLabel: 'participants',
  },
  {
    id: 'ladder',
    label: 'Ladder',
    shortLabel: 'Ladder',
    tagline: 'Courts of 4, round robin, then ladder up or down',
    description: 'Participants split into courts of 4, each playing a round robin. Afterward the top 2 of each court move up a court, the bottom 2 move down — Court 1 is always the top tier. Unlike every other format here, only Round 1 can be generated up front: this produces every court\'s round robin, but who lands in which court next depends on those results and is managed live once the tournament starts. Singles or doubles only — not a fit for team rosters.',
    icon: 'ListOrdered',
    minParticipants: 8,
    maxParticipants: 64,
    needsGrouping: false,
    disallowsTeams: true, // one slot per court seat — a multi-player team roster doesn't fit this model
    // Beyond fixed-roster team sports (blocked generically via disallowsTeams
    // + played_by === 'team' in formatEligibility.js), Poker doesn't fit the
    // courts-of-4 head-to-head model either despite being played_by:
    // 'individual' — called out explicitly since played_by alone can't catch it.
    disallowedSports: ['Poker'],
    supportsLadderRounds: true, // organizer-facing "how many ladder rounds" setting — descriptive only, since only Round 1 is ever concretely generated
    resultView: RESULT_VIEW.LIST,
    participantLabel: 'participants',
  },
];

export const getFormatById = (id) => TOURNAMENT_FORMATS.find((f) => f.id === id) || null;

export const isFormatImplemented = (id) => TOURNAMENT_FORMATS.some((f) => f.id === id);
